import { useQuery } from '@tanstack/react-query';
import capitalize from 'lodash/capitalize';

import { useEcomStoreSelector } from '@/data';
import {
  EnumCustomerType,
  GProduct,
  SortBySelectOption,
  TreezStore,
} from '@/types';
import { buildSearchBodyFromParams, useSearchService } from '@/opensearch';
import { PRODUCT_FILTER_ATTRIBUTES } from '@/constants';
import { getProductsFromOpenSearchResponse } from '@/utils';

import { useIsServerSide } from './useIsServerSide';

interface UseGetProductByCategoryProps {
  store?: TreezStore;
  customerType?: EnumCustomerType;

  products?: GProduct[];

  // limit and sort
  sortBy?: SortBySelectOption;
  limit: number;

  // filters
  filterByStore?: boolean;
  filterByCategory?: string;
  filterByBrand?: string;
  filterByGroupId?: string;
  filterByOnSale?: boolean;
  locationSlug?: string;
  effects?: string;
  flavors?: string;
  generals?: string;
}

export const useProductsByCategory = ({
  store,
  customerType,
  limit,
  sortBy,
  products: initialProducts,
  filterByStore,
  filterByCategory,
  filterByBrand,
  filterByGroupId,
  filterByOnSale,
  locationSlug,
  effects,
  flavors,
  generals,
}: UseGetProductByCategoryProps) => {
  const { customer_type, provider_inventory_location_id } =
    useEcomStoreSelector(['customer_type', 'provider_inventory_location_id']);
  const { isServerSide } = useIsServerSide();
  const customerTypeToUse = isServerSide ? customerType : customer_type;

  const dispensaryName = store?.short_name || '';

  const queryKey = [
    'productsByCategory',
    dispensaryName,
    customerTypeToUse,
    filterByCategory,
    filterByGroupId,
    filterByOnSale,
    filterByBrand,
    filterByStore,
    provider_inventory_location_id,
    effects,
    flavors,
    generals,
  ];

  const { search } = useSearchService({
    selectedStore: store,
    selectedCustomerType: customerTypeToUse,
  });

  // in case that locationSlug is present, check if there is a selected store (dispensaryName)
  const isEnabled = filterByStore && (locationSlug ? !!dispensaryName : true);

  const { data, isLoading, error, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const sortParam = {
        sort:
          sortBy && sortBy.value !== 'default'
            ? `${sortBy.value.field}${capitalize(sortBy.value.order)}`
            : '',
      };

      const groupIdParam = {
        [PRODUCT_FILTER_ATTRIBUTES._GROUP_ID]: filterByGroupId
          ? [filterByGroupId]
          : [],
      };

      const categoryParam = {
        [PRODUCT_FILTER_ATTRIBUTES._CATEGORY]:
          filterByCategory && filterByCategory !== 'default'
            ? [filterByCategory.toUpperCase()]
            : [],
      };

      const brandParam = {
        [PRODUCT_FILTER_ATTRIBUTES._BRAND]:
          filterByBrand && filterByBrand !== 'default'
            ? [filterByBrand.toUpperCase()]
            : [],
      };

      const onSaleParam = {
        [PRODUCT_FILTER_ATTRIBUTES._ON_SALE]: filterByOnSale ? ['true'] : [],
      };

      const effectsParam = {
        [PRODUCT_FILTER_ATTRIBUTES._EFFECTS]: effects
          ? effects.split(',').map(effect => effect.trim().toUpperCase())
          : [],
      };

      const flavorsParam = {
        [PRODUCT_FILTER_ATTRIBUTES._FLAVORS]: flavors
          ? flavors.split(',').map(flavor => flavor.trim().toUpperCase())
          : [],
      };

      const generalsParam = {
        [PRODUCT_FILTER_ATTRIBUTES._GENERALS]: generals
          ? generals.split(',').map(general => general.trim().toUpperCase())
          : [],
      };

      const { searchBody } = buildSearchBodyFromParams({
        searchParams: {
          ...sortParam,
          ...groupIdParam,
          ...categoryParam,
          ...brandParam,
          ...onSaleParam,
          ...effectsParam,
          ...flavorsParam,
          ...generalsParam,
        },
        filters: {
          isAboveThreshold: [true],
          isHideFromMenu: [false],
        },
        limit: limit ? parseInt(limit?.toString(), 10) : 20,
        omitAggs: true,
      });

      const result = await search({ searchBody });

      const products = getProductsFromOpenSearchResponse({
        result,
        isSortingApplied: !!sortBy,
      });

      return products;
    },

    enabled: isEnabled,
    initialData: initialProducts,
  });

  return {
    error,
    isError,
    isLoading,
    products: data ?? [],
    total: 0,
  };
};
